import { useEffect, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { PurposeNearbySphere } from 'types/purpose';
import { getSpherePurposeNearby } from '../api/purposeApi';

export interface UsePurposeNearbyResult {
  siblings: PurposeNearbySphere[];
  children: PurposeNearbySphere[];
  loading: boolean;
}

const EMPTY: PurposeNearbySphere[] = [];

/**
 * Loads `GET /spheres/:id/purpose/nearby` for the Purpose parent picker (spec
 * EAINT-995 SC9). One hook rather than a fetch in each host, because both
 * `PurposeSection` and `NeedsAHomeListMolecule` open the same dialog and need the
 * same two lists — the second for whichever unplaced sphere the person clicked.
 *
 * `enabled` keeps the read off the wire until the dialog is actually opened: the
 * lists are only ever read by the picker, so fetching on mount would cost every
 * sphere page a request nobody looks at.
 *
 * Fails soft — an error leaves both lists empty rather than surfacing a message.
 * Nearby candidates are a convenience; the picker's org-wide search still reaches
 * every sphere, so a failed read costs the person a shortcut, not the task.
 */
export function usePurposeNearby(
  graphClient: ApiClient | null,
  sphereId: string | null,
  enabled: boolean
): UsePurposeNearbyResult {
  const [siblings, setSiblings] = useState<PurposeNearbySphere[]>(EMPTY);
  const [children, setChildren] = useState<PurposeNearbySphere[]>(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !graphClient || !sphereId) {
      setSiblings(EMPTY);
      setChildren(EMPTY);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    getSpherePurposeNearby(graphClient, sphereId)
      .then((nearby) => {
        if (cancelled) return;
        setSiblings(nearby.siblings ?? EMPTY);
        setChildren(nearby.children ?? EMPTY);
      })
      .catch(() => {
        if (cancelled) return;
        setSiblings(EMPTY);
        setChildren(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [graphClient, sphereId, enabled]);

  return { siblings, children, loading };
}
